// single-post.jsx — 単一記事 (HBR 風 editorial layout)
// PHP 側で window.HB_POST = {
//   en, jp_label, title, content_html, date, thumbnail, summary,
//   archive_url, archive_label, related: [{id,title,date,permalink,thumbnail}],
//   cats: [{name,url}], tags: [{name,url}],
//   author: {name,avatar,bio} | null
// } を注入する前提
//
// レイアウト方針 (HBR articleモデル):
// - 記事カラム幅 720px に統一（画像も本文も同じ幅で「詰まった編集記事」の質感）
// - H1 は Mincho 系 serif で大きめ・行間広め
// - 冒頭に「この記事のポイント (Summary)」box（無ければ非表示）
// - 著者 byline は冒頭+末尾の2回表示（HBR 流）
// - sticky TOC は右レール、本文と並列

(function(){
  const { HBLayout, hbcp, fontEN, fontENSans, fontJP, fontMono, HB_BASE } = window;
  // 編集メディア向け Serif フォントスタック（タイトル・H2 用）
  const fontSerif = '"Zen Kaku Gothic New", "Noto Sans JP", sans-serif';

  function SinglePostPage() {
    const d = window.HB_POST || {};
    const {
      en='Article', jp_label='記事', title='', content_html='', date='', thumbnail='', thumbnail_srcset='', thumbnail_sizes='',
      summary='', deck='', readtime_min=0, last_verified='',
      archive_url='#', archive_label='一覧へ戻る', related=[],
      cats=[], tags=[], author=null, profile_people=null, audience_chips=[], sidebar_lps=[],
      sidebar_affiliates=[],
    } = d;

    const articleRef = React.useRef(null);
    const [toc, setToc] = React.useState([]);
    const [shareUrl, setShareUrl] = React.useState('');
    const [lightbox, setLightbox] = React.useState(null);  // P3-5
    const [showBottomBar, setShowBottomBar] = React.useState(false);  // P3-6

    React.useEffect(() => {
      try { setShareUrl(window.location.href); } catch(e){}
      const root = articleRef.current; if (!root) return;
      const items = [];
      root.querySelectorAll('h2, h3').forEach((h, i) => {
        if (!h.id) h.id = `hb-h-${i}`;
        items.push({ id: h.id, level: h.tagName === 'H2' ? 2 : 3, text: h.textContent });
      });
      setToc(items);

      // P3-5: 本文中の <img> を lightbox 対応に
      root.querySelectorAll('img').forEach(img => {
        if (img.dataset.lightbox === 'bound') return;
        img.dataset.lightbox = 'bound';
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', (e) => {
          e.preventDefault();
          setLightbox({ src: img.currentSrc || img.src, alt: img.alt || '' });
        });
      });

      // P4-1: 旧記事の <p><code>multi-line</code></p> を <pre class="hb-code"> に昇格
      // 旧 Elementor / HTML 記事の writer は単一バッククォートで複数行の
      // ディレクトリ構成例 / コード断片を書いていることが多い。WordPress / wpautop は
      // それを `<p><code>...with newlines...</code></p>` として渡してくるため、
      // ブラウザは inline code として 1 行に潰してしまい、レイアウトが崩壊する。
      //
      // 検出: <p> の唯一の子が <code> で、code 内容に改行 or 100 文字以上 → block 化
      // 出力: <figure class="wp-block-code hb-code hb-code-plain hb-code-theme-dark">
      //         <pre><code class="language-plain">{元のテキスト}</code></pre>
      //       </figure>
      // これで blog-ingest-blocks.css の .hb-code 系スタイル（横スクロール / dark / monospace）
      // が自動で当たる。
      root.querySelectorAll('p > code').forEach(c => {
        if (c.dataset.codeUpgraded) return;
        const text = c.textContent || '';
        // 単行・短いものは inline code として残す（用語強調 / コマンド名 etc.）
        const isMultiLine = text.includes('\n');
        const isLong      = text.length > 90;
        if (!isMultiLine && !isLong) return;

        // 親 <p> がこの <code> 以外の意味あるテキストを持つ場合は触らない（混在 paragraph）。
        // ただし、wpautop / Markdown 処理が triple-backtick (```) を処理し損ねて
        // <p>``<code>...</code>``</p> という形で残っている場合がある。両脇の
        // 「純粋なバッククォート＋ホワイトスペース」だけのテキストノードは無視する。
        const p = c.parentElement;
        if (!p || p.tagName !== 'P') return;
        const otherSiblings = Array.from(p.childNodes).filter(n => {
          if (n === c) return false;
          if (n.nodeType === 3) {
            const t = n.nodeValue || '';
            if (/^[\s`]*$/.test(t)) return false;  // whitespace + backtick only → 無視
          }
          return true;
        });
        if (otherSiblings.length > 0) return;

        c.dataset.codeUpgraded = '1';
        const figure = document.createElement('figure');
        figure.className = 'wp-block-code hb-code hb-code-plain hb-code-theme-dark';
        const pre = document.createElement('pre');
        const newCode = document.createElement('code');
        newCode.className = 'language-plain';
        newCode.textContent = text.replace(/\r\n|\r/g, '\n').replace(/^\n+/, '').replace(/\n+$/, '');
        pre.appendChild(newCode);
        figure.appendChild(pre);
        p.replaceWith(figure);
      });
    }, [content_html]);

    // P3-6: scroll 位置に応じて bottom bar の表示制御
    React.useEffect(() => {
      const onScroll = () => {
        const y = window.scrollY;
        const total = document.documentElement.scrollHeight - window.innerHeight;
        const pct = total > 0 ? y / total : 0;
        setShowBottomBar(y > 600 && pct < 0.85);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // hb-bottom-bar 表示中は chat toggle を上に lift するため body class を toggle。
    // chat-widget.css 側で body.hb-bottom-bar-visible .hb-chat-toggle { bottom: 76px }。
    React.useEffect(() => {
      document.body.classList.toggle('hb-bottom-bar-visible', showBottomBar);
      return () => document.body.classList.remove('hb-bottom-bar-visible');
    }, [showBottomBar]);

    // P3-5: ESC で lightbox 閉じる
    React.useEffect(() => {
      if (!lightbox) return;
      const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
      window.addEventListener('keydown', onKey);
      // body scroll-lock
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }, [lightbox]);

    return (
      <HBLayout>
        {/* ===== Article header (kicker + H1 + byline) =====
            class="hb-article-hero" — モバイル padding 調整等は CSS 側 (.hb-article-hero) で */}
        <section className="hb-article-hero">
          <div style={{maxWidth:720, margin:'0 auto'}}>
            {/* Breadcrumb */}
            <nav style={{fontFamily:fontMono, fontSize:11, letterSpacing:'.04em', color:hbcp.inkLight, marginBottom:24, display:'flex', flexWrap:'wrap', gap:6}}>
              <a href={HB_BASE} style={{color:hbcp.inkLight, textDecoration:'none'}}>TOP</a>
              <span>/</span>
              <a href={archive_url} style={{color:hbcp.inkLight, textDecoration:'none'}}>{jp_label}</a>
              {cats[0] && (<>
                <span>/</span>
                <a href={cats[0].url||'#'} style={{color:hbcp.inkLight, textDecoration:'none'}}>{cats[0].name}</a>
              </>)}
            </nav>

            {/* P1-2 Kicker — EB Garamond italic small caps · deep red · 0.18em tracking */}
            {cats[0] && (
              <a href={cats[0].url || '#'} className="article-kicker"
                 style={{display:'inline-block', fontFamily:fontEN, fontWeight:500, fontSize:13, letterSpacing:'.18em', color:'#A0241F', textDecoration:'none', marginBottom:18, textTransform:'uppercase'}}>
                {cats[0].name}
              </a>
            )}

            {/* Serif 大型 H1 */}
            <h1 className="hb-article-title article-title" style={{fontFamily:fontSerif, fontWeight:700, fontSize:'clamp(28px, 3.4vw, 40px)', lineHeight:1.4, letterSpacing:'.04em', margin:'0 0 16px', color:hbcp.ink}}>{title}</h1>

            {/* P1-2 Deck — italic-serif standfirst (Economist 流) */}
            {deck && (
              <p className="article-deck" style={{fontFamily:fontSerif, fontWeight:400, fontSize:'clamp(15px, 1.4vw, 18px)', lineHeight:1.6, color:'#4a4a4a', margin:'0 0 24px', maxWidth:'38em'}}>
                {deck}
              </p>
            )}

            {/* P1-2 Byline — author photo + name + meta line (date · last-verified · read-time) */}
            <div style={{display:'flex', alignItems:'center', gap:14, paddingBottom:18, borderBottom:`1px solid ${hbcp.line}`, flexWrap:'wrap'}}>
              {author && author.avatar && (
                <img className="article-author-photo" src={author.avatar} alt=""
                     style={{width:44, height:44, borderRadius:'50%', display:'block', flex:'0 0 auto', border:'1px solid #d8d2c5'}}/>
              )}
              <div className="article-author-block" style={{display:'flex', flexDirection:'column', gap:3}}>
                {(profile_people || (author ? [author] : [])).map((person, i) => (
                  <span key={`${person.kind}-${person.user_id || person.name}-${i}`} style={{fontFamily:fontJP, fontWeight:600, fontSize:14, color:hbcp.ink}}>{person.label ? `${person.label}：` : ''}{person.name}</span>
                ))}
                <div className="article-meta-secondary" style={{display:'flex', alignItems:'center', gap:8, fontSize:12, color:hbcp.inkLight, flexWrap:'wrap', fontFamily:fontMono}}>
                  <span>{date} 公開</span>
                  {last_verified && (<>
                    <span>·</span>
                    <span className="article-last-verified" style={{color:'#A0241F'}}>{last_verified} 最終更新</span>
                  </>)}
                  {readtime_min > 0 && (<>
                    <span>·</span>
                    <span>{readtime_min} 分で読了</span>
                  </>)}
                </div>
              </div>
              {/* P1-2 Share — X / LinkedIn / Copy URL (right-aligned)
                  ※ 生グリフ (𝕏 / in / ⎘) はフォントフォールバック先で巨大化するため、
                     ブランド SVG を明示寸法で inline する */}
              <div style={{marginLeft:'auto', display:'flex', gap:8}}>
                <a href={`https://x.com/intent/tweet?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}&text=${encodeURIComponent(title)}`} target="_blank" rel="noopener" aria-label="X" style={{width:32, height:32, borderRadius:'50%', border:'1px solid #d8d2c5', display:'grid', placeItems:'center', textDecoration:'none', color:hbcp.ink}}>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`} target="_blank" rel="noopener" aria-label="LinkedIn" style={{width:32, height:32, borderRadius:'50%', border:'1px solid #d8d2c5', display:'grid', placeItems:'center', textDecoration:'none', color:hbcp.ink}}>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
                <button onClick={()=>{ try{ navigator.clipboard?.writeText(window.location.href); }catch(e){} }}
                        aria-label="リンクコピー" type="button"
                        style={{width:32, height:32, borderRadius:'50%', border:'1px solid #d8d2c5', display:'grid', placeItems:'center', background:'transparent', cursor:'pointer', color:hbcp.ink, padding:0}}>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                </button>
              </div>
            </div>
          </div>

          {/* P2-6: Audience chips — self-segmentation bar (PV09 / PV20 等で利用) */}
          {audience_chips && audience_chips.length > 0 && (
            <div style={{maxWidth:720, margin:'24px auto 0'}}>
              <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.28em', color:hbcp.inkLight, fontWeight:600, marginBottom:8}}>JUMP TO</div>
              <ul className="hb-audience-chips" style={{display:'flex', flexWrap:'wrap', gap:8, listStyle:'none', padding:0, margin:0}}>
                {audience_chips.map((chip, i) => (
                  <li key={i}>
                    <a href={chip.anchor}
                       onClick={(e)=>{ e.preventDefault(); const el = document.querySelector(chip.anchor); if (el) el.scrollIntoView({behavior:'smooth', block:'start'}); }}
                       style={{
                         display:'inline-flex', alignItems:'center', gap:6,
                         padding:'8px 16px', background:'#fff', border:`1px solid ${hbcp.line}`,
                         borderRadius:'999px', textDecoration:'none', color:hbcp.ink,
                         fontFamily:fontJP, fontSize:13, fontWeight:500,
                         transition:'all .15s ease',
                       }}>
                      {chip.label}
                      <span style={{fontFamily:fontEN, color:'#A0241F', fontSize:13}}>↓</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Hero image — 本文カラムと同じ 720px 幅で控えめに */}
          {thumbnail && (
            <div style={{maxWidth:720, margin:'32px auto 0', aspectRatio:'16/9', overflow:'hidden', background:hbcp.paper}}>
              <img src={thumbnail} srcSet={thumbnail_srcset || undefined} sizes={thumbnail_srcset ? (thumbnail_sizes || undefined) : undefined} alt="" fetchPriority="high" decoding="async" style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}}/>
            </div>
          )}
        </section>

        {/* ===== Article body (with right-rail TOC on PC) =====
            モバイルで section 既定 padding-left/right (10px) すらも余白に感じるという
            ユーザー fb (2026-05-08)。class hb-article-body-section を付与し、≤640px で
            L/R を 0、上下も圧縮して画面端ぎりぎりまで本文を寄せる。 */}
        <section className="hb-article-body-section">
          <div className="hb-article-row" style={{maxWidth:1140, margin:'0 auto', display:'grid', gridTemplateColumns:(toc.length>0 || sidebar_lps.length>0 || sidebar_affiliates.length>0) ? 'minmax(0,720px) 300px' : 'minmax(0,720px)', gap:56, alignItems:'flex-start', justifyContent:'center'}}>
            <div style={{minWidth:0}}>
              {/* Summary box (この記事のポイント) — HBR 流の TL;DR 配置 */}
              {summary && summary.trim() && (
                <div className="hb-article-summary" style={{padding:'22px 26px', background:hbcp.blueGhost, borderLeft:`3px solid ${hbcp.blue}`, marginBottom:36}}>
                  <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', color:hbcp.blue, fontWeight:700, marginBottom:10}}>● SUMMARY</div>
                  <div style={{fontFamily:fontJP, fontSize:14.5, lineHeight:1.95, color:hbcp.ink, fontWeight:500}} dangerouslySetInnerHTML={{__html: summary}}/>
                </div>
              )}

              <article ref={articleRef} className="hb-article" style={{fontSize:16, lineHeight:1.95, color:hbcp.ink}}
                       dangerouslySetInnerHTML={{ __html: content_html }} />

              {/* Author bio（記事末尾。HBR 流の "About the author" box） */}
              {(profile_people || (author ? [author] : [])).map((person, i) => (
                <aside key={`${person.kind || 'author'}-${i}`} className={`hb-person-card hb-person-card--${person.kind || 'author'}`} aria-label={`${person.label || '著者'}紹介`}
                  style={{marginTop:i === 0 ? 64 : 16, padding:28, background:person.kind === 'reviewer' ? '#f1f6f3' : hbcp.paper, border:`1px solid ${person.kind === 'reviewer' ? '#d9e5dc' : hbcp.line}`, display:'grid', gridTemplateColumns:person.avatar ? '80px minmax(0,1fr)' : 'minmax(0,1fr)', gap:20, alignItems:'flex-start'}}>
                  {person.avatar && <img src={person.avatar} alt={`${person.name}のプロフィール写真`} style={{width:80, height:80, objectFit:'cover', borderRadius:'50%', display:'block'}}/>}
                  <div style={{minWidth:0, overflowWrap:'anywhere'}}>
                    <div style={{fontFamily:fontJP, fontSize:12, letterSpacing:'.12em', color:person.kind === 'reviewer' ? '#3d6650' : hbcp.blue, fontWeight:700, marginBottom:8}}>{person.label || '著者'}</div>
                    <h4 style={{fontFamily:fontJP, fontWeight:700, fontSize:18, margin:'0 0 8px'}}>{person.url ? <a href={person.url} style={{color:'inherit', textDecoration:'underline', textUnderlineOffset:3}}>{person.name}</a> : person.name}</h4>
                    {person.name_en && <p lang="en" style={{fontSize:13, lineHeight:1.5, margin:'-4px 0 8px', color:hbcp.inkSoft, letterSpacing:'.02em'}}>{person.name_en}</p>}
                    {(person.title || person.credentials) && <p style={{fontSize:13, lineHeight:1.7, margin:'0 0 8px', color:hbcp.inkSoft}}>{[person.title, person.credentials].filter(Boolean).join(' · ')}</p>}
                    {person.linkedin && <p style={{fontSize:13, lineHeight:1.6, margin:'0 0 8px'}}><a href={person.linkedin} target="_blank" rel="noopener me" style={{color:hbcp.blue, textDecoration:'underline', textUnderlineOffset:3}}><i className="fab fa-linkedin" aria-hidden="true" style={{marginRight:6}}/>LinkedIn</a></p>}
                    {person.bio && <p style={{fontSize:13.5, lineHeight:1.85, color:hbcp.inkSoft, margin:0, whiteSpace:'pre-line'}}>{person.bio}</p>}
                  </div>
                </aside>
              ))}
            </div>

            {(toc.length > 0 || sidebar_lps.length > 0 || sidebar_affiliates.length > 0) && (
              <aside className="hb-article-rail" style={{position:'sticky', top:96, alignSelf:'flex-start', fontSize:12.5, lineHeight:1.55, display:'flex', flexDirection:'column', gap:32}}>
                {/* TOC */}
                {toc.length > 0 && (
                  <div className="hb-toc">
                    {/* P2-5: Bloomberg "In this article" 風 — top rule + EB-style kicker + decimal-zero numerals */}
                    <div style={{borderTop:`2px solid ${hbcp.ink}`, marginBottom:14}}/>
                    <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.28em', color:hbcp.ink, fontWeight:700, marginBottom:6, textTransform:'uppercase'}}>Inside this report</div>
                    <div style={{fontFamily:fontEN, fontSize:11, color:hbcp.inkLight, marginBottom:14, letterSpacing:'.02em'}}>{toc.filter(t=>t.level===2).length} sections</div>
                    <ol className="hb-toc-list" style={{listStyle:'none', padding:0, margin:0}}>
                      {toc.map((it, i) => {
                        const h2idx = toc.slice(0, i+1).filter(t => t.level === 2).length;
                        return (
                          <li key={it.id} className={`hb-toc-item hb-toc-${it.level}`} style={{borderBottom: it.level===2 ? `1px solid ${hbcp.line}` : 'none'}}>
                            <a href={`#${it.id}`}
                               onClick={(e)=>{ e.preventDefault(); const el = document.getElementById(it.id); if (el) el.scrollIntoView({behavior:'smooth', block:'start'}); }}
                               style={{
                                 display:'flex', alignItems:'baseline', gap:10,
                                 padding: it.level===2 ? '12px 0' : '8px 0 8px 26px',
                                 minHeight:44,
                                 color: it.level===2 ? hbcp.ink : hbcp.inkSoft,
                                 textDecoration:'none',
                                 fontWeight: it.level===2 ? 600 : 400,
                                 transition:'color .15s ease',
                               }}>
                              {it.level===2 && <span style={{fontFamily:fontEN, color:'#A0241F', flex:'0 0 22px', fontSize:13, fontWeight:500}}>{String(h2idx).padStart(2,'0')}</span>}
                              <span style={{flex:1}}>{it.text}</span>
                            </a>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                )}

                {/* Affiliate ads in sidebar */}
                {sidebar_affiliates.length > 0 && (
                  <div style={{display:'flex', flexDirection:'column', gap:16}}>
                    <div style={{borderTop:`2px solid ${hbcp.ink}`}}/>
                    <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.28em', color:hbcp.ink, fontWeight:700, textTransform:'uppercase'}}>PR / Affiliate</div>
                    {sidebar_affiliates.map((aff, idx) => (
                      <a key={idx} href={aff.url}
                         target="_blank"
                         rel="noopener sponsored"
                         className="hb-sidebar-affiliate__cta"
                         data-affiliate-service={aff.service || 'generic'}
                         data-affiliate-label={aff.label || ''}
                         style={{display:'block', textDecoration:'none', color:hbcp.ink, border:`1px solid ${hbcp.line}`, background:'#fff', overflow:'hidden', transition:'box-shadow .15s'}}>
                        {aff.image_url && (
                          <div style={{overflow:'hidden', background:hbcp.paper, textAlign:'center', padding:'8px'}}>
                            <img src={aff.image_url} alt={aff.label} loading="lazy" style={{maxWidth:'100%', height:'auto', display:'inline-block'}}/>
                          </div>
                        )}
                        <div style={{padding:'12px 14px 14px'}}>
                          <h4 style={{fontFamily:fontJP, fontWeight:700, fontSize:13, lineHeight:1.5, margin:'0 0 5px', color:hbcp.ink}}>{aff.label}</h4>
                          {aff.description && <p style={{fontSize:11, lineHeight:1.6, color:hbcp.inkLight, margin:'0 0 8px'}}>{aff.description}</p>}
                          <span style={{display:'inline-block', fontSize:11, fontFamily:fontJP, fontWeight:700, color:'#fff', background:'#2c4a7a', padding:'5px 12px', borderRadius:3}}>
                            {aff.link_text || '詳細を見る'} →
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {/* LP バナー (2026-05-10) — 管理画面で記事に紐付けた LP を右レール下部に表示 */}
                {sidebar_lps.length > 0 && (
                  <div className="hb-sidebar-lps" style={{display:'flex', flexDirection:'column', gap:18}}>
                    <div style={{borderTop:`2px solid ${hbcp.ink}`}}/>
                    <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.28em', color:hbcp.ink, fontWeight:700, textTransform:'uppercase'}}>Sponsored / Related Service</div>
                    {sidebar_lps.map(lp => (
                      <a key={lp.id} href={lp.url || '#'}
                         className="hb-sidebar-lp-card"
                         target={lp.url && lp.url.startsWith('http') ? '_blank' : '_self'}
                         rel="noopener"
                         style={{display:'block', textDecoration:'none', color:hbcp.ink, border:`1px solid ${hbcp.line}`, background:'#fff', overflow:'hidden'}}>
                        {lp.image && (
                          <div style={{aspectRatio:'16/9', overflow:'hidden', background:hbcp.paper}}>
                            <img src={lp.image} alt={lp.title} loading="lazy" style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}}/>
                          </div>
                        )}
                        <div style={{padding:'14px 16px 16px'}}>
                          <h4 style={{fontFamily:fontJP, fontWeight:700, fontSize:13.5, lineHeight:1.5, margin:'0 0 6px', color:hbcp.ink}}>{lp.title}</h4>
                          {lp.desc && <p style={{fontSize:11.5, lineHeight:1.65, color:hbcp.inkLight, margin:'0 0 10px'}}>{lp.desc}</p>}
                          <span style={{display:'inline-block', fontSize:11, fontFamily:fontENSans, fontWeight:700, letterSpacing:'.06em', color:lp.color || hbcp.blue, borderBottom:`1px solid ${lp.color || hbcp.blue}`, paddingBottom:2}}>
                            {lp.btn_text || '詳しく見る'} →
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </aside>
            )}
          </div>

          <style>{`
            /* ============================================================
             * Single-post 主要セクション — 編集しやすさのため inline style を CSS に抽出
             * ============================================================
             * DevTools で要素クリック → ここの class 定義を見れば値の出所が一目瞭然。
             * 値の調整はここを直接編集するだけ。inline style に !important で上書きする
             * パターンを排除し、CSS 側を single source of truth にする。
             *
             *   .hb-article-hero          パンくず + H1 + byline (記事冒頭)
             *   .hb-article-body-section  本文 + sticky TOC レール
             *   .hb-article-meta-row      Tags + Share (本文末)
             *   .hb-article-cta           ブルー CTA (お問い合わせ誘導)
             *   .hb-article-related       関連記事グリッド
             */
            .hb-article-hero          { padding: 48px 32px 32px;     background: ${hbcp.white}; }
            .hb-article-body-section  { padding: 20px 32px 64px;     background: ${hbcp.white}; }
            .hb-article-meta-row      { padding: 24px 32px 56px;     background: ${hbcp.white}; border-bottom: 1px solid ${hbcp.line}; }
            .hb-article-cta           { padding: 72px 32px;          background: ${hbcp.blue}; color: #fff; }
            .hb-article-related       { padding: 72px 32px 96px;     background: ${hbcp.paper}; }

            /* モバイル (≤640px): 各セクションの余白を圧縮。
               🚨 padding 累積問題への対応 (2026-05-08):
                 旧: section 10px + .hb-article 10px + 内部 section 10px = 40px 削減
                 新: 本文ゾーン (.hb-article-body-section) のみ L/R = 0 にし、
                     gutter は内側の .hb-article (padding:10px) が 1 回だけ担う。
                     hero / meta-row / cta / related は装飾用で本文累積に効かないので
                     10px L/R padding を維持。 */
            @media (max-width: 640px) {
              .hb-article-hero          { padding: 10px 10px 24px; }
              .hb-article-body-section  { padding: 12px 0 40px; }    /* ← L/R=0 (内側 .hb-article が 10px gutter を担う) */
              .hb-article-meta-row      { padding: 20px 10px 32px; }
              .hb-article-cta           { padding: 40px 10px; }
              .hb-article-related       { padding: 32px 10px 56px; }

              /* .hb-article 配下の Elementor 由来 inner section の余分な padding を中和。
                 グローバル "section { padding: 10px L/R !important }" が孫 section に
                 効いて累積するのを止める。 */
              .hb-article section,
              .hb-article main,
              .hb-article .elementor-widget-container,
              .hb-article .e-con-inner {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }

              /* 関連記事カード — モバイル 1 列レイアウトで隣接カードの border-top と
                 border-bottom が二重線に見える事故を解消。2 枚目以降は border-top を消し、
                 最後の 1 枚は border-bottom も消す → 全体で 1 本の連続した border 風に。 */
              .hb-article-related-card { border-top: none !important; }
              .hb-article-related-card:first-child { border-top: 1px solid ${hbcp.line} !important; }
            }

            /* ============================================================
             * 本文 (.hb-article) 内タイポ
             * ============================================================ */
            /* HBR 風の「詰まった記事」読み心地 — 行間ゆったり、タイポ階層強め、画像は本文幅 */
            .hb-article p { margin: 0 0 1.4em; }
            .hb-article > p:first-of-type { font-size:18px; line-height:1.95; color:${hbcp.ink}; font-weight:500; }
            .hb-article h2 {
              font-family: ${fontSerif}; font-weight:700; font-size:26px; line-height:1.45;
              margin:0px 0 18px;
              letter-spacing:.04em; scroll-margin-top: 96px; color:${hbcp.ink};
            }
            .hb-article h3 {
              font-family: ${fontJP}; font-weight:700; font-size:18px; line-height:1.5;
              margin:32px 0 12px; scroll-margin-top: 96px; color:${hbcp.ink};
              padding-left:14px; border-left:3px solid ${hbcp.blue};
            }
            .hb-article h4 { font-family:${fontJP}; font-weight:700; font-size:16px; margin:24px 0 10px; }
            .hb-article a { color:${hbcp.blue}; text-decoration: underline; text-underline-offset: .2em; text-decoration-thickness: 1px; }
            .hb-article a:hover { text-decoration-thickness: 2px; }
            .hb-article ul, .hb-article ol { padding-left: 1.4em; margin: 0 0 1.4em; }
            .hb-article li { margin: .4em 0; }

            /* Pull-quote 大型引用（章中の呼吸点） */
            .hb-article blockquote {
              margin: 36px 0; padding: 20px 0 20px 28px;
              border-left: 4px solid ${hbcp.blue}; background: transparent;
              font-family: ${fontSerif}; font-size: 22px; line-height: 1.7; color: ${hbcp.ink}; font-weight: 500;
            }
            .hb-article blockquote p:last-child { margin-bottom: 0; }
            .hb-article blockquote cite { display:block; margin-top:12px; font-family:${fontENSans}; font-style:normal; font-size:12px; letter-spacing:.16em; color:${hbcp.inkLight}; }

            /* 画像は本文と同じ幅 / 余白も控えめに（記事として詰まる） */
            .hb-article img { max-width: 100%; height: auto; margin: 28px 0; }
            .hb-article figure { margin: 28px 0; }
            .hb-article figure img { margin: 0; }
            .hb-article figcaption { font-family:${fontMono}; font-size:11px; color:${hbcp.inkLight}; margin-top:8px; line-height:1.6; }

            /* コードブロック */
            .hb-article pre { background: ${hbcp.ink}; color: #fff; padding: 18px 22px; overflow-x: auto; font-family: ${fontMono}; font-size: 13px; line-height: 1.6; margin: 24px 0; }
            .hb-article code { font-family: ${fontMono}; font-size: 13.5px; background: ${hbcp.paper}; padding: 2px 6px; border-radius: 2px; }
            .hb-article pre code { background: transparent; padding: 0; }

            /* 区切り線 */
            .hb-article hr { border: 0; border-top: 1px solid ${hbcp.line}; margin: 48px 0; }

            /* テーブル（数字の見せ方） */
            .hb-article table { border-collapse: collapse; width: 100%; margin: 28px 0; font-size: 14px; }
            .hb-article th, .hb-article td { border: 1px solid ${hbcp.line}; padding: 10px 14px; text-align: left; }
            .hb-article th { background: ${hbcp.paper}; font-weight: 700; }

            /* 既存ハテナ ブログCSS との互換 — highlight-box / case-study / section-content は触らないで活かす */
            .hb-article .highlight-box { background: ${hbcp.blueGhost}; border-left: 3px solid ${hbcp.blue}; padding: 18px 22px; margin: 24px 0; }
            .hb-article .highlight-title { font-family:${fontJP}; font-weight:700; font-size:14px; color:${hbcp.blue}; margin-bottom:8px; }

            /* 本文中の 目次 (Elementor 移行記事の冒頭 TOC を再装飾) — 番号付き青丸スタイル */
            .hb-article .hb-article-toc { background:${hbcp.paper}; padding:16px 26px; margin:20px 0; border-left:3px solid ${hbcp.blue}; counter-reset: hb-toc; }
            .hb-article .hb-article-toc-title { font-family:${fontENSans}; font-size:11px; letter-spacing:.28em; color:${hbcp.blue}; font-weight:700; margin-bottom:12px; }
            .hb-article .hb-article-toc ol { list-style:none; padding-left:0; margin:0; }
            .hb-article .hb-article-toc li { padding:7px 0 7px 32px; position:relative; margin:0; border-bottom:1px solid ${hbcp.line}; }
            .hb-article .hb-article-toc li:last-child { border-bottom:none; }
            .hb-article .hb-article-toc li::before {
              counter-increment: hb-toc;
              content: counter(hb-toc, decimal-leading-zero);
              position:absolute; left:0; top:50%; transform:translateY(-50%);
              font-family:${fontMono}; font-size:11px; color:${hbcp.blue}; font-weight:600;
            }
            .hb-article .hb-article-toc a { color:${hbcp.ink}; text-decoration:none; font-size:14px; }
            .hb-article .hb-article-toc a:hover { color:${hbcp.blue}; text-decoration:underline; }
            .hb-article .case-study { background: ${hbcp.paper}; border-left: 3px solid ${hbcp.blue}; padding: 18px 22px; margin: 18px 0; border-radius: 2px; }
            .hb-article .case-study h4 { color: ${hbcp.blue}; margin-top:0; }
            .hb-article .section-content { margin: 0 0 1.4em; }
            .hb-article .section-title { font-family:${fontSerif}; font-weight:700; font-size:24px; }

            @media (max-width: 880px) {
              .hb-article-row { grid-template-columns: 1fr !important; }
              /* 右レール (TOC + LP バナー) は本文下に積む。sticky 解除。 */
              .hb-article-rail { position: static !important; flex-direction: column !important; gap: 32px !important; margin: 32px 0 !important; }
              .hb-sidebar-lps { padding: 0 16px !important; }
            }

            /* P2-5: TOC editorial hover state — Bloomberg / Economist 流 */
            .hb-toc-list a { transition: color .15s ease; }
            .hb-toc-list a:hover { color: #A0241F !important; text-decoration: underline; text-underline-offset: 4px; }
            .hb-toc-list a:hover span:first-child { color: ${hbcp.ink} !important; }

            /* P2-6: Audience chips hover state */
            .hb-audience-chips a:hover {
              border-color: ${hbcp.ink} !important;
              background: ${hbcp.paper} !important;
            }
            /* Mobile: TOC を本文下に表示するときの体裁 */
            @media (max-width: 880px) {
              .hb-toc { position: static !important; margin: 32px 0; padding: 0 16px; }
              .hb-toc-list .hb-toc-2 { border-bottom: 1px solid ${hbcp.line}; }
            }

            /* mobile: article body section の L/R 余白を撤去 (variant-d-loader の section[style*=padding] 28/28 より高い specificity が必要) */
            @media (max-width: 640px) {
              section.hb-article-body-section[style] { padding: 10px 0 36px !important; }
            }

            /* ===== The Economist 風 モバイル組版（≤640px） =====
               日本語編集記事として読み心地を絞り込む。
               欧文 Economist の数値感（17px / 1.5）を CJK 換算で 15px / 1.85 に置換。
               左右ガターは variant-d-loader 側で一括 20px → 本文と画像が同じ視覚幅になる。 */
            @media (max-width: 640px) {
              /* 記事タイトル — clamp 最小 28px は CJK では大きいので 24px へ */
              .hb-article-title { font-size: 24px !important; line-height: 1.35 !important; letter-spacing:.04em !important; margin: 0 0 18px !important; }

              /* Body — モバイル可読性 fb (2026-05-14) を反映、15px → 16px / lead 16 → 17 */
              .hb-article { font-size: 16px !important; line-height: 1.85 !important; }
              .hb-article p { margin: 0 0 1.15em !important; }
              .hb-article > p:first-of-type { font-size: 17px !important; line-height: 1.85 !important; font-weight: 500 !important; }

              /* H2/H3/H4 階層をモバイル比で圧縮 */
              .hb-article h2 {
                font-size: 21px !important; line-height: 1.4 !important;
                margin: 0px 0 12px !important; padding-top: 20px !important;
              }
              .hb-article h3 {
                font-size: 15.5px !important; line-height: 1.5 !important;
                margin: 24px 0 8px !important;
                padding-left: 10px !important; border-left-width: 2px !important;
              }
              .hb-article h4 { font-size: 14.5px !important; margin: 20px 0 8px !important; }

              /* Blockquote — 装飾を控えめに */
              .hb-article blockquote {
                margin: 24px 0 !important; padding: 12px 0 12px 16px !important;
                font-size: 17px !important; line-height: 1.7 !important;
                border-left-width: 3px !important;
              }

              /* リスト — インデントを CJK 向けに詰める */
              .hb-article ul, .hb-article ol { padding-left: 1.2em !important; margin: 0 0 1.15em !important; }
              .hb-article li { margin: .25em 0 !important; }

              /* 画像・キャプション — 縦余白を圧縮 */
              .hb-article img, .hb-article figure { margin: 20px 0 !important; }
              .hb-article figcaption { font-size: 10.5px !important; }

              /* Code blocks */
              .hb-article pre { font-size: 12px !important; padding: 12px 14px !important; margin: 18px 0 !important; }
              .hb-article code { font-size: 12.5px !important; }

              /* テーブル */
              .hb-article table { font-size: 13px !important; margin: 20px 0 !important; }
              .hb-article th, .hb-article td { padding: 7px 9px !important; }

              /* 装飾ブロック（ハテナ ブログ既存 CSS 互換） */
              .hb-article .highlight-box, .hb-article .case-study { padding: 14px 16px !important; margin: 18px 0 !important; }
              .hb-article .highlight-title { font-size: 13px !important; }
              .hb-article .section-title { font-size: 19px !important; }

              /* 区切り線 */
              .hb-article hr { margin: 32px 0 !important; }

              /* SUMMARY box（この記事のポイント） */
              .hb-article-summary { padding: 16px 18px !important; margin-bottom: 26px !important; }
              .hb-article-summary > div:last-child { font-size: 13.5px !important; line-height: 1.85 !important; }
            }
          `}</style>
        </section>

        {/* ===== Tags + Share ===== */}
        <section className="hb-article-meta-row">
          <div style={{maxWidth:720, margin:'0 auto', display:'flex', flexWrap:'wrap', gap:24, alignItems:'flex-end', justifyContent:'space-between'}}>
            {tags.length > 0 ? (
              <div>
                <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', color:hbcp.inkLight, marginBottom:10}}>TOPICS</div>
                <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
                  {tags.map(t => (
                    <a key={t.name} href={t.url || '#'} style={{padding:'6px 12px', background:hbcp.paper, color:hbcp.ink, fontFamily:fontJP, fontSize:12, fontWeight:500, textDecoration:'none', border:`1px solid ${hbcp.line}`}}>#{t.name}</a>
                  ))}
                </div>
              </div>
            ) : <div/>}

            <div>
              <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', color:hbcp.inkLight, marginBottom:10, textAlign:'right'}}>SHARE</div>
              <div style={{display:'flex', gap:8}}>
                <a href={`https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`} target="_blank" rel="noopener" aria-label="X" style={{width:36, height:36, border:`1px solid ${hbcp.line}`, display:'grid', placeItems:'center', textDecoration:'none', color:hbcp.ink}}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener" aria-label="Facebook" style={{width:36, height:36, border:`1px solid ${hbcp.line}`, display:'grid', placeItems:'center', textDecoration:'none', color:hbcp.ink}}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073"/></svg>
                </a>
                <button onClick={()=>{ navigator.clipboard?.writeText(shareUrl); }}
                  aria-label="Copy URL"
                  style={{width:36, height:36, border:`1px solid ${hbcp.line}`, background:'transparent', display:'grid', placeItems:'center', cursor:'pointer', padding:0, color:hbcp.ink}}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ===== End CTA ===== */}
        <section className="hb-article-cta">
          <div style={{maxWidth:1080, margin:'0 auto', display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:48, alignItems:'flex-end'}}>
            <div>
              <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', opacity:.8, marginBottom:14}}>● CONTACT</div>
              <h3 style={{fontFamily:fontJP, fontWeight:800, fontSize:'clamp(22px, 2.6vw, 30px)', lineHeight:1.45, margin:'0 0 14px'}}>
                記事の内容について、もっと詳しく聞いてみる。
              </h3>
              <p style={{fontSize:14, lineHeight:1.9, opacity:.9, margin:0, maxWidth:520}}>
                kintone・会計・AI・編集の現場の話を、30分・無料で具体的に伺います。
              </p>
            </div>
            <div style={{display:'grid', gap:12}}>
              <a href={`${HB_BASE}/contact`} style={{padding:'16px 24px', background:'#fff', color:hbcp.blue, textDecoration:'none', fontFamily:fontJP, fontSize:14, fontWeight:700, display:'flex', justifyContent:'space-between'}}>
                <span>無料相談を予約する</span><span style={{fontFamily:fontEN}}>→</span>
              </a>
              <a href={`${HB_BASE}/document-request`} style={{padding:'16px 24px', background:'transparent', color:'#fff', border:'1px solid rgba(255,255,255,.6)', textDecoration:'none', fontFamily:fontJP, fontSize:14, fontWeight:600, display:'flex', justifyContent:'space-between'}}>
                <span>サービス資料を請求する</span><span style={{fontFamily:fontEN}}>↗</span>
              </a>
            </div>
          </div>
        </section>

        {/* ===== Related ===== */}
        {related.length > 0 && (
          <section className="hb-article-related">
            <div style={{maxWidth:1280, margin:'0 auto'}}>
              <div className="hb-section-head">
                <span className="hb-section-head-en">Related</span>
                <span className="hb-section-head-jp">関連記事</span>
              </div>
              <div className="hb-article-related-grid" style={{display:'grid', gridTemplateColumns:`repeat(${Math.min(related.length, 3)}, 1fr)`, gap:24}}>
                {related.map(r => (
                  <a key={r.id} href={r.permalink} className="hb-article-related-card" style={{display:'flex', flexDirection:'column', gap:12, background:'#fff', textDecoration:'none', color:hbcp.ink, border:`1px solid ${hbcp.line}`}}>
                    {r.thumbnail && (
                      <div style={{aspectRatio:'16/9', overflow:'hidden', background:hbcp.paper}}>
                        <img src={r.thumbnail} alt="" style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}}/>
                      </div>
                    )}
                    <div style={{padding:r.thumbnail?'12px 18px 18px':'18px'}}>
                      <div style={{fontFamily:fontMono, fontSize:11, color:hbcp.inkLight, marginBottom:8}}>{r.date}</div>
                      <h3 style={{fontFamily:fontJP, fontWeight:700, fontSize:14.5, lineHeight:1.55, margin:0}}>{r.title}</h3>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}
        {/* P3-5: Lightbox overlay */}
        {lightbox && (
          <div onClick={() => setLightbox(null)}
               style={{position:'fixed', inset:0, background:'rgba(11,28,72,0.92)', zIndex:9500, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px', cursor:'zoom-out'}}>
            <button onClick={() => setLightbox(null)} aria-label="閉じる"
                    style={{position:'fixed', top:18, right:18, width:44, height:44, borderRadius:'50%', background:'rgba(255,255,255,0.15)', color:'#fff', border:'none', fontSize:24, lineHeight:1, cursor:'pointer'}}>×</button>
            <img src={lightbox.src} alt={lightbox.alt}
                 onClick={(e) => e.stopPropagation()}
                 style={{maxWidth:'95vw', maxHeight:'90vh', objectFit:'contain', display:'block', cursor:'default'}}/>
          </div>
        )}

        {/* P3-6: Reading-mode bottom bar (続きを読む / 関連記事) */}
        <div className="hb-bottom-bar" style={{
          position:'fixed', left:0, right:0, bottom:0, zIndex:50,
          background:hbcp.ink, color:'#fff',
          padding:'10px 20px',
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
          transform: showBottomBar ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform .3s ease',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.18)',
        }}>
          <div style={{display:'flex', alignItems:'center', gap:10, minWidth:0}}>
            <span style={{fontFamily:fontENSans, fontSize:9, letterSpacing:'.28em', color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', flex:'0 0 auto'}}>READ ON</span>
            <span style={{fontFamily:fontJP, fontSize:13, fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>関連記事もどうぞ</span>
          </div>
          <div style={{display:'flex', gap:8, flex:'0 0 auto'}}>
            <a href={archive_url}
               style={{padding:'8px 14px', fontSize:12, color:'#fff', textDecoration:'none', border:'1px solid rgba(255,255,255,0.3)', fontFamily:fontJP, fontWeight:500}}>
              一覧
            </a>
            <a href={`${HB_BASE}/contact`}
               style={{padding:'8px 14px', fontSize:12, color:hbcp.ink, background:'#fff', textDecoration:'none', fontFamily:fontJP, fontWeight:600}}>
              無料相談
            </a>
          </div>
        </div>

        <style>{`
          @media (max-width: 640px) {
            .hb-bottom-bar { padding: 8px 14px !important; }
            .hb-bottom-bar a { padding: 6px 12px !important; font-size: 11.5px !important; }
            .hb-bottom-bar > div:first-child > span:last-child { display: none; }
          }
        `}</style>
      </HBLayout>
    );
  }

  Object.assign(window, { SinglePostPage });
})();

// FORCE-REUPLOAD-20260512-110202: lftp 0-byte skip bug — bump mtime for prod re-upload (LP banner JSX)
